"use client";

import { useCallback, useEffect, useState } from "react";
import MediaUploader from "../../../../common/media-uploader";
import { useFormatCurrency } from "../../../../common/format-currency";

import styles from "../index.module.css";
import paystackLogo from "../../../../../assets/images/paystackLogo.png";
import swwipeLogo from "../../../../../assets/images/swwipeLogo.jpg";

const PaymentMethod = ({
  cart,
  onProceed,
  accessPoint,
  paymentMethod,
  setPaymentMethod,
  disableStoreProductPrice,
  paymentGateway,
  setPaymentGateway,
  navLocation,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const marketplaceOnlinePayment = navLocation === "marketplace";
  const disableOnlinePayment =
    disableStoreProductPrice && !marketplaceOnlinePayment;
  const canUseOfflineBankTransfer = Boolean(
    !marketplaceOnlinePayment &&
      accessPoint?.settings?.enableStoreOfflinePayment &&
      accessPoint?.invoiceBankAccount?.[0],
  );
  const canPlaceOrderWithoutPayment = Boolean(
    !marketplaceOnlinePayment &&
      accessPoint?.settings?.enableOrderPlacementWithoutPayment,
  );

  const [error, setError] = useState("");
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  /* ---------------------------------------------
     Active Gateways
  ----------------------------------------------*/
  const activeGateways = ["PAYSTACK", "SWWIPE"];

  const gatewayLogos = {
    PAYSTACK: paystackLogo?.src,
    SWWIPE: swwipeLogo?.src,
  };

  const handleMediaUploadSuccess = (mediaData) => {
    setError("");
    setUploadedMedia(mediaData);
  };

  const handleChange = useCallback((e) => {
    setError("");
    setPaymentMethod(e.target.value);
  }, []);

  /* ---------------------------------------------
     Submit
  ----------------------------------------------*/
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (marketplaceOnlinePayment && paymentMethod !== "ONLINE") {
        setError("Marketplace orders can only be paid online.");
        return;
      }

      if (!paymentMethod) {
        setError("Please select a payment method.");
        return;
      }

      if (paymentMethod === "BANK_TRANSFER") {
        const bankAccount = accessPoint?.invoiceBankAccount?.[0];

        if (!uploadedMedia.length) {
          setError("Please upload your receipt before submitting.");
          return;
        }

        onProceed("BANK_TRANSFER", uploadedMedia, bankAccount);
        return;
      }

      if (paymentMethod === "ONLINE") {
        if (activeGateways.length === 1) {
          setPaymentGateway(activeGateways[0]);
        }

        if (activeGateways.length > 1 && !paymentGateway) {
          setError("Please select a payment gateway.");
          return;
        }

        onProceed("ONLINE");
        return;
      }

      onProceed(paymentMethod);
    },
    [
      paymentMethod,
      uploadedMedia,
      paymentGateway,
      accessPoint,
      marketplaceOnlinePayment,
    ],
  );

  /* ---------------------------------------------
     Payment Method Guard
  ----------------------------------------------*/
  useEffect(() => {
    if (marketplaceOnlinePayment) {
      setPaymentMethod("ONLINE");
      return;
    }

    if (disableOnlinePayment) {
      setPaymentMethod("NO_PAYMENT");
    }
  }, [disableOnlinePayment, marketplaceOnlinePayment]);

  return (
    <div className="auth-form-container1">
      <h3>Payment Method</h3>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="radio-box">
          {/* ONLINE */}
          {!disableOnlinePayment && (
            <>
              {!marketplaceOnlinePayment && (
                <div className="radio-input">
                  <label>
                    <input
                      type="radio"
                      value="ONLINE"
                      name="paymentMethod"
                      checked={paymentMethod === "ONLINE"}
                      onChange={handleChange}
                    />
                    Pay Online
                  </label>
                </div>
              )}

              {/* GATEWAY SELECTION */}
              {paymentMethod === "ONLINE" && activeGateways.length > 1 && (
                <div className={styles.gatewayOptions}>
                  {activeGateways.map((gateway) => (
                    <div
                      key={gateway}
                      className={`${styles.gatewayCard} ${
                        paymentGateway === gateway ? styles.activeGateway : ""
                      }`}
                      onClick={() => {
                        setPaymentGateway(gateway);
                        setError("");
                      }}
                    >
                      <div className={styles.gatewayLeft}>
                        <img
                          src={gatewayLogos[gateway]}
                          alt={gateway}
                          className={styles.gatewayLogo}
                        />
                        <span>{gateway}</span>
                      </div>

                      <div className={styles.gatewayCheckbox}>
                        <div
                          className={`${styles.checkboxCircle} ${
                            paymentGateway === gateway ? styles.checked : ""
                          }`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* BANK TRANSFER */}
              {canUseOfflineBankTransfer && (
                <div className="radio-input">
                  <label>
                    <input
                      type="radio"
                      value="BANK_TRANSFER"
                      name="paymentMethod"
                      checked={paymentMethod === "BANK_TRANSFER"}
                      onChange={handleChange}
                    />
                    Offline Bank Transfer
                  </label>
                </div>
              )}

              {/* BANK TRANSFER BOX */}
              {canUseOfflineBankTransfer && paymentMethod === "BANK_TRANSFER" && (
                <div className="payment-container">
                  <div className="payment-box">
                    <div className="payment-text">
                      Please transfer{" "}
                      <strong>
                        {formatCurrency(cart.totalAmount, cart.currency)}
                      </strong>{" "}
                      to the bank account below.
                    </div>

                    {accessPoint?.invoiceBankAccount?.[0] && (
                      <div className="payment-info">
                        <h1>{accessPoint.invoiceBankAccount[0].accountNo}</h1>
                        <h2>{accessPoint.invoiceBankAccount[0].bankName}</h2>
                        <p>{accessPoint.invoiceBankAccount[0].accountName}</p>
                      </div>
                    )}

                    <MediaUploader
                      onSuccess={handleMediaUploadSuccess}
                      setError={setError}
                      setIsUploading={setIsUploading}
                      supportedFiles={["image/*,video/*,application/pdf"]}
                      allowMultipleUpload
                      destination="attachments"
                      uploadText="Upload Payment Receipt"
                      presetDocuments={uploadedMedia}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* NO PAYMENT */}
          {canPlaceOrderWithoutPayment && (
            <div className="radio-input">
              <label>
                <input
                  type="radio"
                  value="NO_PAYMENT"
                  name="paymentMethod"
                  checked={paymentMethod === "NO_PAYMENT"}
                  onChange={handleChange}
                />
                Place order without payment
              </label>
            </div>
          )}
        </div>

        <div className="login-container">
          <button
            type="submit"
            className="auth-submit-button"
            disabled={isUploading || !paymentMethod}
          >
            {isUploading ? "Uploading..." : "Place Order"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentMethod;
